import { alkisToggleState, setPublicLayers } from '@projects/pages/detail/AlkisMap/layersState';
import { useApiPublicLayers } from '@projects/pages/detail/AlkisMap/useApiImap';
import { invalidate } from '@react-three/fiber';
import { getRenderOrder } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnapshot } from 'valtio';
import { buildTiles } from './helper';
import { Tile } from './Tile';
import type { TileProps } from './Tile/types';

export const Map: FC = () => {
  const mapStyle = useSelector(viewer3dSelectors.mapStyle);
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const { enabled: alkisEnabled } = useSnapshot(alkisToggleState);

  const layers = useApiPublicLayers({
    pageSize: 100,
  });

  useEffect(() => {
    if (layers) {
      setPublicLayers(layers);
    }
  }, [layers]);

  const currentZoomRef = useRef<number | undefined>(undefined);
  const tiles = useMemo(() => {
    if ((mapStyle !== 'NONE' || alkisEnabled) && cameraViewport) {
      return buildTiles(mapStyle, cameraViewport, currentZoomRef.current);
    }
    return [];
  }, [mapStyle, alkisEnabled, cameraViewport]);

  useEffect(() => {
    if (tiles.length > 0) {
      currentZoomRef.current = tiles[0].zoom;
    }
  }, [tiles]);

  // Retain previous zoom level tiles during zoom transitions to prevent any blank flashes
  const [retainedTiles, setRetainedTiles] = useState<TileProps[]>([]);
  const activeTilesRef = useRef<TileProps[]>([]);
  const retentionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const currentZoom = tiles[0]?.zoom;
    const previousZoom = activeTilesRef.current[0]?.zoom;

    if (
      currentZoom !== undefined &&
      previousZoom !== undefined &&
      currentZoom !== previousZoom &&
      activeTilesRef.current.length > 0
    ) {
      setRetainedTiles(activeTilesRef.current);

      if (retentionTimerRef.current) {
        clearTimeout(retentionTimerRef.current);
      }
      retentionTimerRef.current = setTimeout(() => {
        setRetainedTiles([]);
        invalidate();
      }, 2500);
    }

    activeTilesRef.current = tiles;
  }, [tiles]);

  useEffect(() => {
    return () => {
      if (retentionTimerRef.current) {
        clearTimeout(retentionTimerRef.current);
      }
    };
  }, []);

  return (
    <group name={Object3dNames.Map} renderOrder={getRenderOrder(Object3dNames.Map)}>
      {/* Retain previous zoom level tiles underneath during transitions to prevent flicker */}
      {retainedTiles.map((tile) => (
        <Tile
          key={`retained-${tile.zoom}-${tile.tileX}-${tile.tileY}`}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...tile}
          positionZ={tile.positionZ - 0.005}
        />
      ))}
      {tiles.map((tile) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Tile key={`${tile.zoom}-${tile.tileX}-${tile.tileY}`} {...tile} />
      ))}
    </group>
  );
};
